import * as React from 'react';

export function useSelection(allItemKeys?: (number | string)[]) {
	const [selected, setSelected] = React.useState<Record<string, true>>({});

	const selectItem = React.useCallback((id: number | string) => {
		setSelected((current) => {
			if (current[id]) {
				return current;
			}
			return ({ ...current, [id]: true as const });
		});
	}, []);

	const deselectItem = React.useCallback((id: number | string) => {
		setSelected((current) => {
			if (!current[id]) {
				return current;
			}
			const next = { ...current };
			delete next[id];
			return next;
		});
	}, []);

	const toggleItem = React.useCallback((id: number | string) => {
		setSelected((current) => {
			const next = { ...current };
			if (!current[id]) {
				next[id] = true;
			} else {
				delete next[id];
			}
			return next;
		});
	}, []);

	const selectAll = React.useCallback(() => {
		if (!allItemKeys) {
			throw new Error('Provide allItemKeys when using selectAll');
		}
		const map = allItemKeys.reduce<Record<string, true>>((_acc, _key) => {
			_acc[_key] = true;
			return _acc;
		}, {});
		setSelected(map);
	}, [allItemKeys]);

	const clearAll = React.useCallback(() => {
		setSelected({});
	}, []);

	const selectedCount = React.useMemo(() => {
		return Object.keys(selected).length;
	}, [selected]);

	const totalCount = allItemKeys?.length ?? 0;

	return React.useMemo(() => {
		return {
			selected,
			selectedCount,
			totalCount,
			toggleItem,
			selectItem,
			deselectItem,
			selectAll,
			clearAll,
		};
	}, [clearAll, deselectItem, selectAll, selectItem, selected, selectedCount, toggleItem, totalCount]);
}
